import "./WidgetWrapper.scss";
import { UserWidget } from "@/common/constants/online.homepagegateway.api";
import { WidgetId } from "@/common/constants/widgetId";
import RecentOrdersWidget from "../widgets/RecentlyViewed/RecentOrdersWidget";
import { LoggingErrorBoundary } from "@chr/common-javascript-logging-error-boundary";
import WidgetErrorState from "./WidgetErrorState";
import { useEffect, useRef, useState } from "react";
import { SeverityType, useSnackbar } from "@chr/eds-react";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { setAddWidgetNotification } from "@/common/actions/widgetActions";
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from "@/react-app-env";
import { WidgetHeader } from "./WidgetHeader";
import { Box, Card, CardContent, Grid } from "@mui/material";
import OrderShipmentSearchWidget from "./Modules/OrderShipmentSearchWidget";
import { getConfiguration } from '@chr/common-web-ui-configuration';
import { AppConfig } from '../../../react-app-env';

interface WidgetWrapperProps {
    widget: UserWidget;
    triggerResize: () => void;
}

export const WidgetWrapper = ({ widget, triggerResize }: WidgetWrapperProps) => {
    const [widgetKey, setWidgetKey] = useState('widgetwrapperkey-' + widget.id);
    const elementRef = useRef(null);
    const addWidgetNotification = useSelector((state: AppState) => state.widgetsState?.addWidgetNotification);
    const snackbar = useSnackbar();
    const translate = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        if (addWidgetNotification && addWidgetNotification.id === widget.id) {
            const snackbarProps = {
                id: 'widgetSnackBar',
                message: translate(`${addWidgetNotification.name} has been added to homepage`),
                action: {
                    onClick: () => {
                        if (elementRef.current) {
                            (elementRef.current as any).scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    },
                    buttonText: translate('View')
                },
                severity: ('positive' as SeverityType),
            };
            snackbar.enqueueSnackbar(snackbarProps);
            dispatch(setAddWidgetNotification(undefined));
        }
    }, [addWidgetNotification])

    const getWidgetContent = (userWidget: UserWidget) => {
        const config = getConfiguration<AppConfig>()
        if (userWidget.widgetId === WidgetId.RecentOrders) {
            return (
                <RecentOrdersWidget
                    userWidget={userWidget}
                    triggerResize={triggerResize}
                />
            )
        }

        // The old order and shipment search widget is being replaced with a new module-based widget.
        // You can find the new implementation in the github repo https://github.com/ch-robinson-internal/online.shared.ui.ordershipmentsearchwidget
        return (
            <div className="fed-module-widget">
                <OrderShipmentSearchWidget
                    userWidget={userWidget}
                    // config={config}
                />
            </div>
        );
    }

    const getWidget = (userWidget: UserWidget) => {
        return (
            <Grid className="widget-wrapper">
                <Card>
                    <CardContent>
                        <WidgetHeader
                            widget={userWidget}
                        />
                        {getWidgetContent(userWidget)}
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    const OnRetryCallback = () => {
        setWidgetKey(widgetKey => widgetKey + 1);
    }

    return (
        <div ref={elementRef} key={widgetKey} data-testid={`widgetwrappertest-${widget.id}`} className="widget-wrapper grid-layout-drag-handle">
            <LoggingErrorBoundary render={() => <WidgetErrorState title={widget.title} retryCallback={OnRetryCallback} />}>
                {getWidget(widget)}
            </LoggingErrorBoundary>
        </div>
    )
}

export default WidgetWrapper;